import {Component, computed, inject, viewChild, signal, ChangeDetectionStrategy} from "@angular/core"
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from "@angular/material/dialog"
import {MatSnackBar} from "@angular/material/snack-bar"
import {MatTable, MatTableModule} from "@angular/material/table"
import {MatTooltipModule} from "@angular/material/tooltip"
import {TypedMatCellDefDirective} from "@app/common/directives"
import {RefreshService} from "@app/common/services/refresh/refresh.service"
import {
    TemplateRevisionsHistoryDialogTemplateRevisionFragment,
    TemplateRevisionsHistoryDialogDeleteTemplateRevisionGQL,
    TemplateRevisionsHistoryDialogTemplateRevisionGQL,
} from "@app/templates/template-revisions-history-dialog/template-revisions-history-dialog.generated"
import {IsNonNull} from "@cm/utils/filter"
import {DialogComponent} from "@common/components/dialogs/dialog/dialog.component"
import {RenameDialogComponent} from "@common/components/dialogs/rename-dialog/rename-dialog.component"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {FormatDatePipe} from "@common/pipes/format-date/format-date.pipe"
import {TimeAgoPipe} from "@common/pipes/time-ago/time-ago.pipe"
import {NotificationsService} from "@common/services/notifications/notifications.service"
import {PermissionsService} from "@common/services/permissions/permissions.service"
import {ContentTypeModel} from "@generated"
import {ActivatedRoute, Router} from "@angular/router"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-template-revisions-history-dialog",
    templateUrl: "./template-revisions-history-dialog.component.html",
    styleUrls: ["./template-revisions-history-dialog.component.scss"],
    imports: [MatTableModule, MatTooltipModule, TimeAgoPipe, FormatDatePipe, TypedMatCellDefDirective],
})
export class TemplateRevisionsHistoryDialogComponent {
    readonly $revisionTable = viewChild.required<MatTable<TemplateRevisionsHistoryDialogTemplateRevisionFragment>>("revisionTableRef")
    displayedColumns = ["revision", "author", "date"]

    readonly $template = signal({revisions: [] as TemplateRevisionsHistoryDialogTemplateRevisionFragment[]})
    readonly notifications = inject(NotificationsService)
    readonly refresh = inject(RefreshService)
    readonly route = inject(ActivatedRoute)
    readonly router = inject(Router)
    private readonly permission = inject(PermissionsService)
    readonly $can = this.permission.$to

    readonly templateRevisionGql = inject(TemplateRevisionsHistoryDialogTemplateRevisionGQL)
    readonly deleteTemplateRevisionGql = inject(TemplateRevisionsHistoryDialogDeleteTemplateRevisionGQL)
    readonly dialogRef = inject(MatDialogRef<RenameDialogComponent>)
    readonly data = inject<{template: {id: string; organization: {id: string}}}>(MAT_DIALOG_DATA)
    private readonly dialog = inject(MatDialog)
    private readonly snackBar = inject(MatSnackBar)

    readonly $canEditRevision = computed(() => this.$can().update.templateRevision({template: this.data.template}))
    readonly $canDeleteRevision = computed(() => this.$can().delete.templateRevision({template: this.data.template}))

    constructor() {
        void this.fetchRevisions()
    }

    async fetchRevisions() {
        const result = await fetchThrowingErrors(this.templateRevisionGql)({
            templateId: this.data.template.id,
        })
        this.$template.set({revisions: result.templateRevisions.filter(IsNonNull)})
    }

    openRevision(revision: TemplateRevisionsHistoryDialogTemplateRevisionFragment) {
        void this.router.navigate(["revisions", revision.id, "edit"], {
            relativeTo: this.route,
            queryParamsHandling: "preserve",
        })
        this.dialogRef.close()
    }

    deleteRevision(revision: TemplateRevisionsHistoryDialogTemplateRevisionFragment) {
        const dialogRef = this.dialog.open(DialogComponent, {
            disableClose: false,
            width: "400px",
            data: {
                title: "Delete revision",
                message: "The template revision will be deleted. " + "This action <strong>cannot be undone</strong>.<br><br>Are you sure you want to continue?",
                confirmLabel: "Delete revision",
                cancelLabel: "Cancel",
            },
        })
        dialogRef.afterClosed().subscribe(async (confirmed) => {
            if (!confirmed) {
                return
            }
            await this.notifications.withUserFeedback(
                async () => {
                    await mutateThrowingErrors(this.deleteTemplateRevisionGql)({
                        templateRevisionId: revision.id,
                    })
                    await this.fetchRevisions()
                    this.refresh.item({...this.data.template, __typename: ContentTypeModel.Template})
                    this.$revisionTable()?.renderRows()
                },
                {success: "Revision deleted successfully", error: "Cannot delete revision"},
            )
        })
    }
}
